import {
  type WorkbookDiagnostic,
  type WorkbookPivotSheet,
} from "@erp/shared-types";
import type { ColumnType } from "../formula";
import { pivotStaticColumns } from "./layout";

interface Field {
  name: string;
  type: ColumnType;
}
type Area = "rows" | "columns" | "values" | "filters";

/** A pivot's planned stages, known once its input has a schema. */
export interface PivotStages {
  input: readonly Field[];
  /** Its Column keys come from the data, so only some output columns are known. */
  dynamic: boolean;
}

const NUMERIC_SUMMARIES = new Set(["sum", "average", "min", "max", "median"]);
const RESERVED_PREFIX = "__wb_";

/** The output columns a pivot's readers can rely on without data. */
export function pivotDeclaredOutput(sheet: WorkbookPivotSheet): Field[] {
  return pivotStaticColumns(sheet.pivot).columns.map(({ name, type }) => ({
    name,
    type,
  }));
}

/** Records a diagnostic on one field of a pivot area. */
function pivotDiagnosticAt(diagnostics: WorkbookDiagnostic[], itemId: string) {
  return (
    code: WorkbookDiagnostic["code"],
    message: string,
    area: Area,
    index: number,
  ) =>
    diagnostics.push({
      code,
      message,
      location: { part: "pivot", itemId, area, index },
    });
}

/** Diagnostics that need no input schema. */
function specDiagnostics(sheet: WorkbookPivotSheet): WorkbookDiagnostic[] {
  const { pivot } = sheet;
  const diagnostics: WorkbookDiagnostic[] = [];
  const at = pivotDiagnosticAt(diagnostics, sheet.id);
  if (!pivot.rows.length && !pivot.values.length)
    diagnostics.push({
      code: "empty-pivot",
      message: "Add a row field or a value to the pivot.",
      location: { part: "item", itemId: sheet.id },
    });
  (["rows", "columns"] as const).forEach((area) =>
    pivot[area].forEach((group, index) => {
      if (group.sortBy.by === "value" && !pivot.values[group.sortBy.value])
        at(
          "pivot-sort-value",
          `'${group.field}' sorts by a value the pivot doesn't have.`,
          area,
          index,
        );
    }),
  );
  if (pivot.columnKeys?.some((key) => key.length !== pivot.columns.length))
    at(
      "pivot-column-keys",
      "Each pinned column must have one label per column field. Pin the columns again.",
      "columns",
      0,
    );
  pivot.values.forEach((value, index) => {
    if (value.name?.toLowerCase().startsWith(RESERVED_PREFIX))
      at(
        "reserved-name",
        `Column names beginning with ${RESERVED_PREFIX} are reserved.`,
        "values",
        index,
      );
  });
  // A new Filter excludes nothing (Is Not One Of no values: every item
  // shows); only one keeping no values is a problem.
  pivot.filters.forEach((filter, index) => {
    if (filter.operator === "is-one-of" && !filter.values?.length)
      at(
        "empty-value-list",
        `The filter on '${filter.column}' lists no values.`,
        "filters",
        index,
      );
  });
  return diagnostics;
}

/** Diagnostics against the input's Fields. */
function fieldDiagnostics(
  sheet: WorkbookPivotSheet,
  input: readonly Field[],
): WorkbookDiagnostic[] {
  const types = new Map(input.map((field) => [field.name, field.type]));
  const diagnostics: WorkbookDiagnostic[] = [];
  const at = pivotDiagnosticAt(diagnostics, sheet.id);
  const unknown = (name: string, area: Area, index: number): boolean => {
    if (types.has(name)) return false;
    at("unknown-field", `Unknown column '${name}'.`, area, index);
    return true;
  };
  (["rows", "columns"] as const).forEach((area) =>
    sheet.pivot[area].forEach((group, index) => {
      if (unknown(group.field, area, index) || !group.dateGroup) return;
      const type = types.get(group.field);
      if (type !== "date" && type !== "datetime" && type !== "text")
        at(
          "pivot-date-group",
          `'${group.field}' isn't a Date, so it can't be grouped by ${group.dateGroup}.`,
          area,
          index,
        );
    }),
  );
  sheet.pivot.values.forEach((value, index) => {
    if (unknown(value.field, "values", index)) return;
    const type = types.get(value.field);
    if (
      NUMERIC_SUMMARIES.has(value.summarize) &&
      type !== "decimal" &&
      type !== "text"
    )
      at(
        "pivot-value-type",
        `'${value.field}' isn't a Number, so it can only be counted.`,
        "values",
        index,
      );
  });
  sheet.pivot.filters.forEach((filter, index) =>
    unknown(filter.column, "filters", index),
  );
  return diagnostics;
}

/** Plans a pivot Worksheet: its Diagnostics and, once its input has a schema, its stages. */
export function planPivot(
  sheet: WorkbookPivotSheet,
  input: readonly Field[] | undefined,
): { stages: PivotStages | null; diagnostics: WorkbookDiagnostic[] } {
  const diagnostics = specDiagnostics(sheet);
  if (!input) return { stages: null, diagnostics };
  const fields = fieldDiagnostics(sheet, input);
  // Field problems come first, in the pivot's own order.
  return {
    stages: { input, dynamic: pivotStaticColumns(sheet.pivot).dynamic },
    diagnostics: [...fields, ...diagnostics],
  };
}
